import React, { useEffect, useState, memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { MobileNav } from '../components/MobileNav';
// Section component for consistent styling
const Section = ({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) => <section className="pt-16 sm:pt-20" data-id="element-2363">
    <h2 className="text-lg sm:text-xl text-[#F5F5F5] mb-8" style={{
    fontFamily: 'Georgia, serif'
  }} data-id="element-2364">
      {title}
    </h2>
    <div className="space-y-5 text-base sm:text-lg text-gray-400 leading-[1.8]" data-id="element-2365">
      {children}
    </div>
  </section>;
export function LifeDecodeOutput() {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('Reader');
  useEffect(() => {
    const name = sessionStorage.getItem('decodeName');
    if (name) setUserName(name);
  }, []);
  const handleDownload = () => {
    // TODO: Generate and download PDF
    // For now, show alert
    alert('PDF download will be implemented with backend integration');
  };
  const handleSharePrivately = () => {
    // TODO: Generate private link
    navigate('/decode/save?action=share-private');
  };
  const handleInvite = () => {
    navigate('/decode/invite');
  };
  return <>
      <Helmet data-id="element-2366">
        <title data-id="element-2367">Life Decode | Lazy Monk Labs</title>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-2368">
        <MobileNav data-id="element-2369" />

        <main className="pt-20 sm:pt-24 pb-32 sm:pb-40" data-id="element-2370">
          <div className="max-w-2xl mx-auto px-6 sm:px-8" data-id="element-2371">
            {/* ENTRY STATE — Minimal header */}
            <header className="py-12 sm:py-16" data-id="element-2372">
              <h1 className="text-2xl sm:text-3xl text-[#F5F5F5] mb-3" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2373">
                Life Decode
              </h1>
              <p className="text-sm text-gray-600" data-id="element-2374">
                A structural map of how your life has unfolded.
              </p>
            </header>

            {/* PRE-READING PAUSE — Critical for psychological slowing */}
            <div className="pb-16 sm:pb-20" data-id="element-2375">
              <p className="text-sm text-gray-500 leading-relaxed" data-id="element-2376">
                This is not a quick read.
              </p>
              <p className="text-sm text-gray-500 leading-relaxed mt-2" data-id="element-2377">
                Most people take this in over multiple sittings. There is no
                need to rush.
              </p>
            </div>

            {/* CONTENT — Long-form document */}

            {/* SECTION 1 — How this pattern formed */}
            <Section title="How this pattern formed" data-id="element-2378">
              <p data-id="element-2379">
                The structure you operate from did not begin with you. It was
                shaped by the environment you were born into — the pressures,
                the silences, the unspoken rules of your early world.
              </p>
              <p data-id="element-2380">
                You learned early that certain ways of being were rewarded, and
                others were not. This was not instruction. It was absorption.
              </p>
              <p data-id="element-2381">
                The pattern that emerged was not a choice. It was an adaptation
                — a way of surviving the specific conditions you found yourself
                in.
              </p>
              <p className="text-[#F5F5F5]" data-id="element-2382">
                This is the origin layer. Everything else builds on top of it.
              </p>
            </Section>

            {/* SECTION 2 — Generational pressures */}
            <Section title="The generational pressures you inherited" data-id="element-2383">
              <p data-id="element-2384">
                Your parents carried their own unresolved structures. Their
                fears, their ambitions, their unlived lives — all of this was
                transmitted to you, often without words.
              </p>
              <p data-id="element-2385">
                You may have been given a role before you could speak: the one
                who would succeed, the one who would stay close, the one who
                would carry what they could not.
              </p>
              <p data-id="element-2386">
                These pressures are not visible in memory. They are visible in
                pattern — in what you feel compelled to do, even when it costs
                you.
              </p>
              <p className="text-[#F5F5F5]" data-id="element-2387">
                You did not choose this inheritance. But you have been living
                inside it.
              </p>
            </Section>

            {/* SECTION 3 — Environments that shaped pace and direction */}
            <Section title="The environments that shaped your pace and direction" data-id="element-2388">
              <p data-id="element-2389">
                Every environment you entered — school, work, relationships —
                either reinforced your pattern or created friction against it.
              </p>
              <p data-id="element-2390">
                When the environment matched your wiring, you accelerated. When
                it didn't, you either adapted or withdrew.
              </p>
              <p data-id="element-2391">
                The places where you thrived were not random. They were
                structurally compatible with how you were already organized.
              </p>
              <p data-id="element-2392">
                The places where you struggled were not failures. They were
                mismatches — environments that demanded a shape you could not
                hold.
              </p>
              <p className="text-[#F5F5F5]" data-id="element-2393">
                Your trajectory was not about effort. It was about fit.
              </p>
            </Section>

            {/* SECTION 4 — Phases of expansion, correction, settling */}
            <Section title="The phases where this structure expanded, corrected, and settled" data-id="element-2394">
              <p data-id="element-2395">
                Your life has moved through distinct phases — not random, but
                structurally inevitable.
              </p>
              <p data-id="element-2396">
                There were periods of expansion, where your pattern was rewarded
                and you moved forward with momentum.
              </p>
              <p data-id="element-2397">
                There were periods of correction, where the pattern hit its
                limits and something had to give.
              </p>
              <p data-id="element-2398">
                And there were periods of settling, where you integrated what
                you had learned and the pace slowed.
              </p>
              <p className="text-[#F5F5F5]" data-id="element-2399">
                These phases were not accidents. They were the structure
                expressing itself across time.
              </p>
            </Section>

            {/* SECTION 5 — What this phase is no longer willing to support */}
            <Section title="What this phase is no longer willing to support" data-id="element-2400">
              <p data-id="element-2401">
                You are now in a phase where certain things that once worked are
                no longer sustainable.
              </p>
              <p data-id="element-2402">
                The compromises you made earlier — the roles you held, the pace
                you maintained, the parts of yourself you suppressed — are
                becoming harder to carry.
              </p>
              <p data-id="element-2403">
                This is not a crisis. It is a structural shift. The pattern is
                asking for something different.
              </p>
              <p className="text-[#F5F5F5]" data-id="element-2404">
                What you do with this information is yours to decide. But the
                structure will not wait indefinitely.
              </p>
            </Section>

            {/* SECTION 6 — What changes when you see the whole structure */}
            <Section title="What changes when you see the whole structure" data-id="element-2405">
              <p data-id="element-2406">
                Seeing this does not fix anything. It does not remove the
                pattern or make the trade-offs disappear.
              </p>
              <p data-id="element-2407">
                But it changes your relationship to what is happening. The
                confusion reduces. The urgency softens.
              </p>
              <p data-id="element-2408">
                You stop asking "why is this happening to me?" and start seeing
                "this is how I was organized to respond."
              </p>
              <p className="text-[#F5F5F5]" data-id="element-2409">
                The pattern doesn't disappear. It becomes something you own.
              </p>
            </Section>

            {/* POST-READ LAYER — Attachment / Download / Share */}
            <section className="pt-24 sm:pt-32 border-t border-[#2B2B2B] mt-16 sm:mt-20" data-id="element-2410">
              {/* TRANSITION COPY */}
              <div className="space-y-4 text-sm text-gray-500 leading-relaxed mb-12" data-id="element-2411">
                <p data-id="element-2412">Most people don't absorb this all at once.</p>
                <p data-id="element-2413">
                  You may want to return to parts of it later, or share it with
                  someone you trust.
                </p>
              </div>

              {/* ACTION CLUSTER */}
              <div className="space-y-8" data-id="element-2414">
                {/* PRIMARY — DOWNLOAD */}
                <div data-id="element-2415">
                  <button onClick={handleDownload} className="px-6 py-3 border border-[#2B2B2B] text-[#F5F5F5] text-sm font-medium hover:border-gray-500 transition-colors" data-id="element-2416">
                    Download as PDF
                  </button>
                  <p className="text-xs text-gray-600 mt-3" data-id="element-2417">
                    For revisiting or reading offline.
                  </p>
                </div>

                {/* SECONDARY — SHARE PRIVATELY */}
                <div data-id="element-2418">
                  <button onClick={handleSharePrivately} className="px-6 py-3 border border-[#2B2B2B] text-gray-400 text-sm hover:border-gray-500 hover:text-[#F5F5F5] transition-colors" data-id="element-2419">
                    Share privately
                  </button>
                  <p className="text-xs text-gray-600 mt-3" data-id="element-2420">
                    Send this to someone who knows you well.
                  </p>
                </div>

                {/* TERTIARY — INVITE */}
                <div className="pt-4" data-id="element-2421">
                  <button onClick={handleInvite} className="text-sm text-gray-500 hover:text-gray-400 transition-colors" data-id="element-2422">
                    Invite someone to decode themselves
                  </button>
                  <p className="text-xs text-gray-600 mt-2" data-id="element-2423">
                    See if this reflects them too.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </>;
}